/**
 * OpenList 树形结构相关的工具函数
 */

import type { OpenListFile, OpenListFolder, FolderContent, OpenListTreeNode } from "@/types/openlist"

/**
 * 生成节点ID
 */
export function generateNodeId(path: string, type: 'folder' | 'file'): string {
  return `${type}-${path.replace(/\//g, '_')}`
}

/**
 * 创建树形节点
 */
export function createTreeNode(
  item: OpenListFolder | OpenListFile, 
  type: 'folder' | 'file',
  level: number,
  parentPath: string
): OpenListTreeNode {
  const path = type === 'folder' ? (item as OpenListFolder).path : (item as OpenListFile).path
  
  return {
    id: generateNodeId(path, type),
    type,
    name: item.name,
    path,
    parentPath,
    level,
    isExpanded: false,
    isLoaded: type === 'file', // 文件不需要加载子内容
    isLoading: false,
    folderData: type === 'folder' ? (item as OpenListFolder) : undefined,
    fileData: type === 'file' ? (item as OpenListFile) : undefined,
    children: []
  }
}

/**
 * 递归更新节点的子节点
 */
export function updateNodeChildren(
  nodes: OpenListTreeNode[], 
  targetPath: string, 
  newChildren: OpenListTreeNode[]
): OpenListTreeNode[] {
  return nodes.map(node => {
    if (node.path === targetPath && node.type === 'folder') {
      return {
        ...node,
        children: newChildren,
        isLoaded: true,
        isLoading: false
      }
    } else if (node.children.length > 0) {
      return {
        ...node,
        children: updateNodeChildren(node.children, targetPath, newChildren)
      }
    }
    return node
  })
}

/**
 * 将文件夹内容转换为树形节点
 */
export function convertFolderContentToTreeNodes(
  folderPath: string, 
  content: FolderContent
): OpenListTreeNode[] {
  const level = folderPath === '/' ? 0 : folderPath.split('/').length - 1
  
  return [
    // 文件夹节点
    ...content.folders.map(folder => 
      createTreeNode(folder, 'folder', level + 1, folderPath)
    ),
    // 文件节点
    ...content.files.map(file => 
      createTreeNode(file, 'file', level + 1, folderPath)
    )
  ]
}

/**
 * 在树中递归搜索
 */
export function searchInTree(nodes: OpenListTreeNode[], term: string): OpenListTreeNode[] {
  const lowerTerm = term.toLowerCase()
  
  return nodes.reduce<OpenListTreeNode[]>((acc, node) => {
    const nameMatches = node.name.toLowerCase().includes(lowerTerm)
    const pathMatches = node.path.toLowerCase().includes(lowerTerm)
    
    // 递归搜索子节点
    const matchingChildren = node.children.length > 0 ? searchInTree(node.children, term) : []
    
    // 如果当前节点匹配或有匹配的子节点，则包含此节点
    if (nameMatches || pathMatches || matchingChildren.length > 0) {
      acc.push({
        ...node,
        children: matchingChildren
      })
    }
    
    return acc
  }, [])
}

/**
 * 展开状态持久化相关函数
 */
export function saveExpandedFolders(subjectId: string, expandedFolders: Set<string>): void {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(
        `expandedFolders_${subjectId}`,
        JSON.stringify(Array.from(expandedFolders))
      )
    } catch (error) {
      console.warn("Failed to save expanded folders to localStorage:", error)
    }
  }
}

export function loadExpandedFolders(subjectId: string): Set<string> {
  if (typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem(`expandedFolders_${subjectId}`)
      if (stored) {
        const expandedArray = JSON.parse(stored)
        return new Set(expandedArray)
      }
    } catch (error) {
      console.warn("Failed to load expanded folders from localStorage:", error)
    }
  }
  return new Set(["/"])
}
